import { queryClient } from '@core/api/query';
import type { Property } from '@core/models/properties';
import { useAnalyticsStore } from '@state/analytics/analytics.store';
import { selectRegion } from '@state/regions/region.selectors';
import { useBoundStore } from '@state/store';
import type { UnavailableSentinelImageDate } from '@utils/imageAvailability';
import {
  getUnavailableSentinelImageDate,
  isUnavailableSentinelImage,
} from '@utils/imageAvailability';
import axios from 'axios';
import type { Dayjs } from 'dayjs';
import { Amplitude } from 'src/amplitude';
import { RemoteSensingApi } from '../../core/api';
import type { ImageAOI, SceneGroup, Source } from '../../core/models/imageAssets';
import { ImageAOIHelper } from '../../core/models/imageAssets/ImageAOI.util';
import dayjs, { DATE_FORMATS } from '../../utils/dateUtils';
import { Actions } from '../actions';
import type { RootState } from '../reducers';
import actions from './actions';
import type { CatalogOptions } from './models';
import { selectAllSceneGroups } from './reducer';
import {
  filterSceneGroups,
  selectSceneOptions,
  selectedSceneGroup,
  selectedSideSceneGroup,
} from './selectors';
import { fetchScenesByAOI, groupImagesByDate, updateReferenceImage } from './utils';

const { setMapViewMode } = Actions;

const MAX_CURSOR_SIZE = 10000;

const IMAGES_BACKFILL_MINUTES = 1440;

const filterAvailableImages = (images: ImageAOI[], exclusionEnabled: boolean): ImageAOI[] =>
  exclusionEnabled ? images.filter((image) => !isUnavailableSentinelImage(image)) : images;

type PreselectedSceneInfo = {
  date: Dayjs;
  source: Source;
};

type PreselectedScenes = {
  default: PreselectedSceneInfo;
  side: PreselectedSceneInfo;
};

type SelectedRegion = ReturnType<typeof selectRegion>;

type AoiTypeMap = {
  property: string;
  region: string;
  cropCycle: string;
};

const resolveAoiTarget = (
  propertyId: string,
  selectedRegion: SelectedRegion,
  selectedSeasonId: string | undefined,
  types: AoiTypeMap
): { aoiId: string; aoiType: string } => {
  const isPropertyLevel = !selectedRegion || selectedRegion.isRoot;

  if (isPropertyLevel && selectedSeasonId) {
    return { aoiId: `${selectedSeasonId}_${propertyId}`, aoiType: types.cropCycle };
  }

  if (isPropertyLevel) {
    return { aoiId: propertyId, aoiType: types.property };
  }

  return { aoiId: selectedRegion?.id ?? propertyId, aoiType: types.region };
};

const fetchAllScenesByAOI = async (
  aoiId: string,
  aoiType: string,
  dateRange: [Date, Date],
  initialCursor: string | null
) => {
  const [startDate, endDate] = dateRange;
  let imagesData: Array<ImageAOI> = [];
  let nextCursor = initialCursor;

  do {
    const response = await fetchScenesByAOI(aoiId, {
      aoiType,
      startDate: dayjs(startDate).format(DATE_FORMATS.USA_FORMAT),
      endDate: dayjs(endDate).format(DATE_FORMATS.USA_FORMAT),
      cursor: nextCursor,
      sources: 'all',
      pageSize: MAX_CURSOR_SIZE,
      loadComposedOf: false,
    });

    if (response.data?.content) {
      imagesData = imagesData.concat(
        response.data.content.map((scene) => ImageAOIHelper.parse(scene))
      );
      nextCursor = response.data.cursor;
    } else {
      nextCursor = null;
    }
  } while (nextCursor);

  return { imagesData, nextCursor };
};

const findSceneByPreselectedInfo = (scenes: SceneGroup[], info: PreselectedSceneInfo) =>
  scenes.find(
    (scene) =>
      dayjs(scene.referenceImage.acquiredAt).startOf('d').isSame(info.date) &&
      scene.referenceImage.source === info.source
  );

const dispatchPreselectedScenes = (
  dispatch,
  scenes: SceneGroup[],
  preselectedInfo: PreselectedScenes
) => {
  const scene = findSceneByPreselectedInfo(scenes, preselectedInfo.default);
  const sideScene = findSceneByPreselectedInfo(scenes, preselectedInfo.side);

  if (scene) {
    dispatch(selectAssetGroup(scene.id));
  } else if (scenes.length) {
    dispatch(selectAssetGroup(scenes[0].id));
  }

  if (sideScene) {
    dispatch(selectAssetGroup(sideScene.id, true));
  } else if (scenes.length) {
    dispatch(selectAssetGroup(scenes[0].id, true));
  }
};

const handleLoadAssetsError = (e: unknown, propertyId: string, dispatch, getState) => {
  if (axios.isCancel(e)) return;

  const status = axios.isAxiosError(e) ? e.response?.status : undefined;
  const cachedProperty = queryClient.getQueryData<Property | undefined>([
    'property',
    { propertyId: propertyId },
  ]);
  const triggerAlert =
    !!cachedProperty?.workspace_use_type &&
    cachedProperty?.workspace_use_type !== 'DEMO' &&
    cachedProperty?.workspace_use_type !== 'TEST' &&
    (!cachedProperty ||
      !dayjs(cachedProperty.created_at).isBetween(
        dayjs().subtract(IMAGES_BACKFILL_MINUTES, 'minute'),
        dayjs()
      ));

  if (triggerAlert) {
    console.error(`Error loading images: ${status}`, {
      property: {
        created_at: cachedProperty?.created_at,
      },
      status,
    });
  }
  dispatch(actions.LOAD_ASSETS.error(e));

  const errorState: RootState = getState();

  if (errorState.ImageAsset.loadAttempts <= 1) {
    dispatch(
      setAssetsDateRange([dayjs().subtract(1, 'month').toDate(), dayjs().toDate()], propertyId)
    );
  }
};

export const loadAssetsAndImages = (
  propertyId: string,
  preselectedInfo?: PreselectedScenes
) => async (dispatch, getState) => {
  const rootState: RootState = getState();
  const boundState = useBoundStore.getState();
  const selectedScene = selectedSceneGroup(rootState);
  const selectedRegion = selectRegion(boundState);
  const selectedSeasonId = boundState.seasons.selectedSeasonId;

  const {
    loadAttempts,
    dateRange: [startDate, endDate],
    sceneCancelToken,
    showDroneImages,
  } = rootState.ImageAsset;

  // Start requests
  sceneCancelToken?.abort();

  const cancelToken = new AbortController();

  dispatch(actions.LOAD_ASSETS.started({ cancelToken, newloadAttempts: loadAttempts + 1 }));
  const initialTime = performance.now();

  try {
    const { aoiId, aoiType } = resolveAoiTarget(propertyId, selectedRegion, selectedSeasonId, {
      property: 'composite_property',
      region: 'composite_region',
      cropCycle: 'composite_crop_cycle_property',
    });

    const { imagesData, nextCursor } = await fetchAllScenesByAOI(
      aoiId,
      aoiType,
      [startDate, endDate],
      rootState.ImageAsset.assetsCursor
    );

    const latestState: RootState = getState();
    const unavailableDates = new Set<UnavailableSentinelImageDate>();
    const availableImages = imagesData.filter((image) => {
      if (!latestState.ImageAsset.s2bImageExclusionEnabled) return true;

      const date = getUnavailableSentinelImageDate(image);
      if (!date) return true;

      unavailableDates.add(date);
      return false;
    });
    const imageGroups = groupImagesByDate(availableImages).toSorted(
      (a, b) => a.referenceImage.imageCloudCoverage - b.referenceImage.imageCloudCoverage
    );

    dispatch(
      actions.LOAD_ASSETS.success({
        data: imageGroups,
        cursor: nextCursor,
        timeToLoad: performance.now() - initialTime,
        unavailableSentinelImageDates: Array.from(unavailableDates).sort(
          (a, b) => Date.parse(a) - Date.parse(b)
        ),
      })
    );

    if (showDroneImages) {
      dispatch(loadDroneImages(propertyId));
    }

    const newState: RootState = getState();
    const newScenes = selectAllSceneGroups(newState.ImageAsset);

    const options = selectSceneOptions(newState);
    const scenesToSearch = filterSceneGroups(newScenes, options, showDroneImages);

    if (!scenesToSearch.length) {
      dispatch(actions.setSelectedSceneGroup(null));
      dispatch(actions.setSideSelectedSceneGroup(null));
      dispatch(actions.setSelectedImages([]));
      dispatch(actions.setSideSelectedImages([]));
    } else if (preselectedInfo) {
      dispatchPreselectedScenes(dispatch, scenesToSearch, preselectedInfo);
    } else if (selectedScene && showDroneImages) {
      dispatch(selectAssetGroup(selectedScene.id));
      dispatch(selectAssetGroup(selectedScene.id, true));
    } else if (scenesToSearch.length) {
      dispatch(selectAssetGroup(scenesToSearch[0].id));
      dispatch(selectAssetGroup(scenesToSearch[0].id, true));
    }
  } catch (e) {
    handleLoadAssetsError(e, propertyId, dispatch, getState);
  }
};

export const loadDroneImages = (propertyId: string) => async (dispatch, getState) => {
  const rootState: RootState = getState();

  const {
    dateRange: [startDate, endDate],
  } = rootState.ImageAsset;

  const boundState = useBoundStore.getState();
  const selectedRegion = selectRegion(boundState);

  const getPropertyImages = !selectedRegion || selectedRegion.isRoot;

  const aoiId = getPropertyImages ? propertyId : selectedRegion?.id;
  const aoiType = getPropertyImages ? 'composite_property' : 'composite_region';

  dispatch(actions.LOAD_DRONE_ASSETS.started());

  try {
    const responseDrone = await RemoteSensingApi.get(
      `aois/drones?start_date=${dayjs(startDate).format(DATE_FORMATS.USA_FORMAT)}&end_date=${dayjs(
        endDate
      ).format(DATE_FORMATS.USA_FORMAT)}&uid=${aoiId}&aoi_type=${aoiType}&size=100000`
    );

    if (responseDrone.data) {
      const groupDrones = groupImagesByDate(
        responseDrone.data.content.map((x) => ImageAOIHelper.parse(x))
      );

      dispatch(
        actions.LOAD_DRONE_ASSETS.success({
          data: groupDrones,
        })
      );
    }
  } catch (error) {
    dispatch(actions.LOAD_DRONE_ASSETS.error(error));
  }
};

export const selectAssetGroup = (sceneGroupId: string | null, sideMap: boolean = false) => (
  dispatch,
  getState
) => {
  const state: RootState = getState();
  const sceneEntities = state.ImageAsset.entities;
  const { compositionStatus, sideCompositionStatus } = state.ImageAsset;
  const { viewMode } = state.Map[sideMap ? 'sideMap' : 'map'];

  // Update Current selected asset group with new asset group
  const newSceneGroup = sceneEntities[sceneGroupId ?? ''];

  const shouldUpdate = sideMap
    ? sideCompositionStatus !== 'loading'
    : compositionStatus !== 'loading';

  if (newSceneGroup && shouldUpdate) {
    Amplitude.trackSelectedSource(newSceneGroup.referenceImage.source, 'Map');

    if (newSceneGroup.referenceImage.source === 'drone' && viewMode !== 'map') {
      dispatch(setMapViewMode('map'));
    }

    if (sideMap) {
      dispatch(actions.setSideSelectedSceneGroup(sceneGroupId));
    } else {
      dispatch(actions.setSelectedSceneGroup(sceneGroupId));
    }

    dispatch(loadSceneGroupComposition(newSceneGroup, sideMap));
  } else if (shouldUpdate) {
    dispatch(actions.setSelectedSceneGroup(null));
    dispatch(actions.setSideSelectedSceneGroup(null));
    dispatch(actions.setSelectedImages([]));
    dispatch(actions.setSideSelectedImages([]));
  }
};

export const updateSelectedImages = (sceneGroup: SceneGroup) => (dispatch) => {
  dispatch(actions.setSelectedImages(sceneGroup.images));
};

export const updateSideSelectedImages = (sceneGroup: SceneGroup) => (dispatch) => {
  dispatch(actions.setSideSelectedImages(sceneGroup.images));
};

export const loadSceneGroupComposition = (sceneGroup: SceneGroup, sideMap: boolean) => async (
  dispatch,
  getState
) => {
  const initialState: RootState = getState();
  const selectedSceneKey = sideMap ? 'selectedSideSceneGroupId' : 'selectedSceneGroupId';
  const setImages = sideMap ? actions.setSideSelectedImages : actions.setSelectedImages;
  const compositionActions = sideMap ? actions.LOAD_SIDE_COMPOSITION : actions.LOAD_COMPOSITION;
  const isCurrentComposition = (latestState: RootState) =>
    latestState.ImageAsset[selectedSceneKey] === initialState.ImageAsset[selectedSceneKey] &&
    latestState.ImageAsset.sceneCancelToken === initialState.ImageAsset.sceneCancelToken;
  const isReferenceExcluded = (latestState: RootState) =>
    latestState.ImageAsset.s2bImageExclusionEnabled &&
    isUnavailableSentinelImage(sceneGroup.referenceImage);

  if (isReferenceExcluded(initialState)) {
    if (initialState.ImageAsset[selectedSceneKey] === sceneGroup.id) {
      dispatch(setImages([]));
    }
    return;
  }

  const state = useBoundStore.getState();
  const selectedRegion = selectRegion(state);
  const selectedSeasonId = state.seasons.selectedSeasonId;
  const propertyId = sceneGroup.referenceImage.aoi.property_id;

  const isComposedOrDrone =
    sceneGroup.images.length > 0 &&
    (Object.keys(sceneGroup.images[0].composedOf).length > 0 ||
      sceneGroup.images[0].source === 'drone');

  if (isComposedOrDrone) {
    dispatch(
      setImages(
        filterAvailableImages(sceneGroup.images, initialState.ImageAsset.s2bImageExclusionEnabled)
      )
    );
    return;
  }

  dispatch(compositionActions.started());

  try {
    dispatch(setImages([]));
    const { aoiId, aoiType } = resolveAoiTarget(propertyId, selectedRegion, selectedSeasonId, {
      property: 'property',
      region: 'region',
      cropCycle: 'crop_cycle_property',
    });
    const { data } = await fetchScenesByAOI(aoiId, {
      aoiType,
      startDate: dayjs.utc(sceneGroup.referenceImage.acquiredAt).format(DATE_FORMATS.USA_FORMAT),
      endDate: dayjs
        .utc(sceneGroup.referenceImage.acquiredAt)
        .add(1, 'd')
        .format(DATE_FORMATS.USA_FORMAT),
      sources: 'all',
      pageSize: MAX_CURSOR_SIZE,
      loadComposedOf: true,
    });

    const parsedData = data.content.map((c) => ImageAOIHelper.parse(c));
    const latestState: RootState = getState();
    // Catalog reloads and selection changes invalidate the request, including its status updates.
    if (!isCurrentComposition(latestState)) return;

    if (isReferenceExcluded(latestState)) {
      dispatch(setImages([]));
    } else {
      const availableImages = filterAvailableImages(
        parsedData,
        latestState.ImageAsset.s2bImageExclusionEnabled
      );
      const newReferenceImage = updateReferenceImage(sceneGroup.referenceImage, availableImages);
      dispatch(actions.updateAssetGroupComposition(sceneGroup.id, newReferenceImage, sideMap));
    }
    dispatch(compositionActions.success());
  } catch (e) {
    const latestState: RootState = getState();
    if (!isCurrentComposition(latestState)) return;

    const fallbackImages = isReferenceExcluded(latestState)
      ? []
      : filterAvailableImages(sceneGroup.images, latestState.ImageAsset.s2bImageExclusionEnabled);
    dispatch(setImages(fallbackImages));
    dispatch(compositionActions.error(e));
  }
};

export const setSelectedImages = (sceneGroup: SceneGroup, images: ImageAOI[]) => (
  dispatch,
  getState
) => {
  const state: RootState = getState();
  const { selectedSceneGroupId } = state.ImageAsset;
  if (sceneGroup.id === selectedSceneGroupId) {
    dispatch(actions.setSelectedImages(images));
  } else {
    dispatch(selectAssetGroup(sceneGroup.id));
    dispatch(actions.setSelectedImages(images));
  }
};

export const setSideSelectedImages = (scene: SceneGroup, images: ImageAOI[]) => (
  dispatch,
  getState
) => {
  const state: RootState = getState();
  const { selectedSideSceneGroupId } = state.ImageAsset;

  if (scene.id === selectedSideSceneGroupId) {
    dispatch(actions.setSideSelectedImages(images));
  } else {
    dispatch(selectAssetGroup(scene.id, true));
    dispatch(actions.setSideSelectedImages(images));
  }
};

export const setAssetsDateRange = (
  dates: [Date, Date],
  propertyId: string,
  preselectedScenes?: PreselectedScenes
) => (dispatch) => {
  const analyticsStore = useAnalyticsStore.getState();
  const { setAnalyticsDateRange } = analyticsStore;

  dispatch({ type: actions.SET_DATE_RANGE, payload: dates });
  dispatch({ type: actions.RESET });
  dispatch(loadAssetsAndImages(propertyId, preselectedScenes));

  // Global scenes date range also changes analytics date ranges
  setAnalyticsDateRange(dates);
};

export const setCatalogOptions = (newOptions: CatalogOptions, sideBySide: boolean) => (
  dispatch,
  getState
) => {
  const state: RootState = getState();
  const { options, optionsSide } = state.ImageAsset;
  const sceneGroup = sideBySide ? selectedSideSceneGroup(state) : selectedSceneGroup(state);

  if (sceneGroup && sceneGroup.referenceImage.imageCloudCoverage > newOptions.cloudCoverage) {
    dispatch(selectAssetGroup(null, sideBySide));
  }

  if (sideBySide) {
    dispatch(actions.setCatalogOptionsSide({ ...optionsSide, ...newOptions }));
  } else {
    dispatch(actions.setCatalogOptions({ ...options, ...newOptions }));
  }
};
